import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type GscMetricRow = { key: string; clicks: number; impressions: number; ctr: number; position: number };

export type GscSnapshotView = {
  siteUrl: string | null;
  refreshedAt: string | null;
  rangeFrom: string | null;
  rangeTo: string | null;
  totals: { clicks: number; impressions: number; ctr: number; position: number };
  prevTotals: { clicks: number; impressions: number; ctr: number; position: number };
  byDay: { date: string; clicks: number; impressions: number }[];
  topQueries: GscMetricRow[];
  topPages: GscMetricRow[];
  error: string | null;
  candidates?: string[];
};

const EMPTY = { clicks: 0, impressions: 0, ctr: 0, position: 0 };

export const getGscSnapshot = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<GscSnapshotView> => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data } = await supabaseAdmin
      .from("gsc_snapshot")
      .select("*")
      .order("refreshed_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!data) {
      return {
        siteUrl: null, refreshedAt: null, rangeFrom: null, rangeTo: null,
        totals: EMPTY, prevTotals: EMPTY, byDay: [], topQueries: [], topPages: [],
        error: null,
      };
    }

    const r = data as Record<string, unknown>;
    return {
      siteUrl: (r.site_url as string) ?? null,
      refreshedAt: (r.refreshed_at as string) ?? null,
      rangeFrom: (r.range_from as string) ?? null,
      rangeTo: (r.range_to as string) ?? null,
      totals: { ...EMPTY, ...(r.totals as object) },
      prevTotals: { ...EMPTY, ...(r.prev_totals as object) },
      byDay: (r.by_day as GscSnapshotView["byDay"]) ?? [],
      topQueries: (r.top_queries as GscMetricRow[]) ?? [],
      topPages: (r.top_pages as GscMetricRow[]) ?? [],
      error: (r.error as string) ?? null,
    };
  });

export const refreshGscNow = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { refreshGscSnapshot } = await import("./gsc.server");
    return await refreshGscSnapshot();
  });

export const setGscProperty = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { siteUrl: string }) => {
    if (!data?.siteUrl || typeof data.siteUrl !== "string") throw new Error("Manjka lastnina.");
    return { siteUrl: data.siteUrl };
  })
  .handler(async ({ context, data }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { listVerifiedProperties } = await import("./gsc.server");
    const allowed = await listVerifiedProperties();
    if (!allowed.includes(data.siteUrl)) throw new Error("Ta lastnina ni preverjena za to stran.");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("app_settings").upsert({ key: "gsc_site_url", value: data.siteUrl });
    if (error) throw new Error(error.message);
    const { refreshGscSnapshot } = await import("./gsc.server");
    return await refreshGscSnapshot();
  });

export const GSC_RANGES = [
  { id: "7", label: "7 dni", days: 7 },
  { id: "28", label: "28 dni", days: 28 },
  { id: "90", label: "90 dni", days: 90 },
  { id: "365", label: "1 leto", days: 365 },
  { id: "1825", label: "5 let", days: 1825 },
  { id: "max", label: "Max", days: "max" as const },
] as const;

export type GscRangeId = (typeof GSC_RANGES)[number]["id"];

export const getGscRange = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { range: string }) => {
    const found = GSC_RANGES.find((r) => r.id === data?.range);
    if (!found) throw new Error("Neveljavno obdobje.");
    return { range: found.id };
  })
  .handler(async ({ context, data }): Promise<GscSnapshotView> => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { fetchGscRange } = await import("./gsc.server");
    const cfg = GSC_RANGES.find((r) => r.id === data.range)!;
    const res = await fetchGscRange(cfg.days);
    return {
      siteUrl: res.siteUrl ?? null,
      refreshedAt: new Date().toISOString(),
      rangeFrom: res.rangeFrom ?? null,
      rangeTo: res.rangeTo ?? null,
      totals: { ...EMPTY, ...(res.totals ?? {}) },
      prevTotals: { ...EMPTY, ...(res.prevTotals ?? {}) },
      byDay: res.byDay ?? [],
      topQueries: res.topQueries ?? [],
      topPages: res.topPages ?? [],
      error: res.error ?? null,
      candidates: res.candidates,
    };
  });
